import React from 'react';
import { Box, Tooltip } from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';

// Define trip type mapping with IDs, labels, and colors
export const tripTypeMapping: Record<string, { id: number, label: string, icon: string, shortform: string, tooltip: string, color: string }> = {
  'MUMBAI_AIRPORT_PICKUP': { id: 1, label: 'Mumbai Airport - Pickup', icon: 'plane-landing', shortform: 'AP-PU', tooltip: 'Mumbai Airport - Pickup', color: '#00BCD4' },
  'MUMBAI_AIRPORT_DROP': { id: 1, label: 'Mumbai Airport - Drop', icon: 'plane-takeoff', shortform: 'AP-DR', tooltip: 'Mumbai Airport - Drop', color: '#00BCD4' },
  'ONE_WAY_PUNE': { id: 2, label: 'One Way - Pune', icon: 'arrow', shortform: '1W-PN', tooltip: 'One Way - Pune', color: '#FF9800' },
  'ONE_WAY_MUMBAI': { id: 2, label: 'One Way - Mumbai', icon: 'arrow', shortform: '1W-MU', tooltip: 'One Way - Mumbai', color: '#FF9800' },
  'OUTSTATION_ROUND_PUNE': { id: 3, label: 'Outstation Round Trip - Pune', icon: 'loop', shortform: 'RT-PN', tooltip: 'Outstation Round Trip - Pune', color: '#4CAF50' },
  'OUTSTATION_ROUND_MUMBAI': { id: 3, label: 'Outstation Round Trip - Mumbai', icon: 'loop', shortform: 'RT-MU', tooltip: 'Outstation Round Trip - Mumbai', color: '#4CAF50' },
  'PUNE_LOCAL_6H': { id: 4, label: 'Pune Local - 6 Hours', icon: 'building', shortform: 'PL-6H', tooltip: 'Pune Local - 6 Hours', color: '#E91E63' },
  'PUNE_LOCAL_9H': { id: 4, label: 'Pune Local - 9 Hours', icon: 'building', shortform: 'PL-9H', tooltip: 'Pune Local - 9 Hours', color: '#E91E63' },
  'PUNE_LOCAL_12H': { id: 4, label: 'Pune Local - 12 Hours', icon: 'building', shortform: 'PL-12H', tooltip: 'Pune Local - 12 Hours', color: '#E91E63' },
  'CUSTOM': { id: 5, label: 'Custom', icon: 'settings', shortform: 'CUSTOM', tooltip: 'Custom Trip', color: '#9C27B0' },
  'PERPAX': { id: 6, label: 'Per Pax Service', icon: 'arrow', shortform: 'PER_PAX', tooltip: 'Per Pax Service', color: '#3F51B5' },

};

// Reverse mapping from ID to trip type
export const serviceIdToTripType: Record<number, string> = {};
Object.entries(tripTypeMapping).forEach(([type, details]) => {
  serviceIdToTripType[details.id] = type;
});

// Helper function to get trip icon component
export const getTripIconComponent = (iconType: string, color: string): React.ReactNode => {
  // Custom rendering to match exactly what's shown in the screenshot
  switch (iconType) {
    case 'plane-takeoff':
      // For AP-DR (Airport Drop) - Takeoff icon
      return (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M2.5 19h19v2h-19v-2zm19.57-9.36c-.21-.8-1.04-1.28-1.84-1.06L14.92 10l-6.9-6.43-1.93.51 4.14 7.17-4.97 1.33-1.97-1.54-1.45.39 1.82 3.16.77 1.33 1.6-.43 5.31-1.42 4.35-1.16L21 11.49c.81-.23 1.28-1.05 1.07-1.85z" fill={color} />
        </svg>
      );
    case 'plane-landing':
      // For AP-PU (Airport Pickup) - Landing icon
      return (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M2.5 19h19v2h-19v-2zm7.18-5.73l4.35 1.16 5.31 1.42 1.6.43.77-1.33 1.83-3.17-1.45-.39-1.97 1.54-4.96-1.33 4.13-7.16-1.93-.51-6.9 6.43-5.31-1.42-.7 1.34 4.32 2.9z" fill={color} />
        </svg>
      );
    case 'arrow':
      // One-way direction icon - showing a single direction arrow
      return (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M14 7l-5 5 5 5V7z" fill="none" />
          <path d="M5 12h14M12 4l8 8-8 8" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
    case 'loop':
      return (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M12 4V1L8 5l4 4V6c3.31 0 6 2.69 6 6 0 1.01-.25 1.97-.7 2.8l1.46 1.46C19.54 15.03 20 13.57 20 12c0-4.42-3.58-8-8-8zm0 14c-3.31 0-6-2.69-6-6 0-1.01.25-1.97.7-2.8L5.24 7.74C4.46 8.97 4 10.43 4 12c0 4.42 3.58 8 8 8v3l4-4-4-4v3z" fill={color} />
        </svg>
      );
    case 'building':
      return (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M15 11V5l-3-3-3 3v2H3v14h18V11h-6zm-8 8H5v-2h2v2zm0-4H5v-2h2v2zm0-4H5V9h2v2zm6 8h-2v-2h2v2zm0-4h-2v-2h2v2zm0-4h-2V9h2v2zm0-4h-2V5h2v2zm6 12h-2v-2h2v2zm0-4h-2v-2h2v2z" fill={color} />
        </svg>
      );
    case 'settings':
      return <SettingsIcon style={{ color, fontSize: '1.2rem' }} />;
    default:
      return (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M16 17.01V10h-2v7.01h-3L15 21l4-3.99h-3zM9 3L5 6.99h3V14h2V6.99h3L9 3z" fill={color} />
        </svg>
      );
  }
};

// Helper functions to get trip icon, shortform, tooltip, and color
export const getTripIcon = (tripType: string): string => {
  // Find the exact match first
  if (tripTypeMapping[tripType]) {
    return tripTypeMapping[tripType].icon;
  }
  
  // If no exact match, try to find a partial match
  for (const [key, value] of Object.entries(tripTypeMapping)) {
    if (tripType.includes(key) || key.includes(tripType)) {
      return value.icon;
    }
  }
  
  // Default icon if no match found
  return 'arrow';
};

export const getTripShortform = (tripType: string): string => {
  // Find the exact match first
  if (tripTypeMapping[tripType]) {
    return tripTypeMapping[tripType].shortform;
  }
  
  // If no exact match, try to find a partial match
  for (const [key, value] of Object.entries(tripTypeMapping)) {
    if (tripType.includes(key) || key.includes(tripType)) {
      return value.shortform;
    }
  }
  
  // Default shortform if no match found
  return tripType;
};

export const getTripTooltip = (tripType: string): string => {
  // Find the exact match first
  if (tripTypeMapping[tripType]) {
    return tripTypeMapping[tripType].tooltip;
  }
  
  // If no exact match, try to find a partial match
  for (const [key, value] of Object.entries(tripTypeMapping)) {
    if (tripType.includes(key) || key.includes(tripType)) {
      return value.tooltip;
    }
  }
  
  // Default tooltip if no match found
  return tripType;
};

export const getTripColor = (tripType: string): string => {
  // Find the exact match first
  if (tripTypeMapping[tripType]) {
    return tripTypeMapping[tripType].color;
  }
  
  // If no exact match, try to find a partial match
  for (const [key, value] of Object.entries(tripTypeMapping)) {
    if (tripType.includes(key) || key.includes(tripType)) {
      return value.color;
    }
  }
  
  // Default color if no match found
  return '#757575';
};

// Helper function for legacy code that uses the old format
export const getLegacyTripShortform = (tripType: string | undefined, request?: string, destination?: string) => {
  // Handle undefined tripType
  if (!tripType) {
    return {
      icon: getTripIconComponent('arrow', '#757575'),
      shortform: 'N/A',
      tooltip: 'Unknown Trip Type',
      color: '#757575'
    };
  }

  // Default values
  let iconType = 'arrow';
  let shortform = '';
  let tooltip = tripType;
  let color = '#757575';

  // Mumbai Airport
  if (tripType === 'Mumbai Airport' || tripType.includes('MUMBAI_AIRPORT')) {
    iconType = request === 'pickup' ? 'plane-landing' : 'plane-takeoff';
    color = '#00BCD4';
    shortform = request === 'pickup' ? 'AP-PU' : 'AP-DR';
    tooltip = `Mumbai Airport - ${request === 'pickup' ? 'Pickup' : 'Drop'}`;
  }
  // One Way
  else if (tripType === 'One Way' || tripType.includes('ONE_WAY')) {
    iconType = 'arrow';
    color = '#FF9800';
    shortform = destination?.includes('Pune') ? '1W-PN' : destination?.includes('Mumbai') ? '1W-MU' : '1W';
    tooltip = `One Way${destination ? ` - ${destination}` : ''}`;
  }
  //perpax
  else if (tripType.toLowerCase().includes('per pax') || tripType.includes('PERPAX')) {
    iconType = 'arrow';
    color = '#3F51B5';
    shortform = destination?.includes('Pune') ? 'PP-PN' : destination?.includes('Mumbai') ? 'PP-MU' : 'per_pax';
    tooltip = 'Per Pax Service';
  }
  // Outstation Round Trip
  else if (tripType === 'Outstation - Round Trip' || tripType.includes('Round Trip') || tripType.includes('OUTSTATION_ROUND')) {
    iconType = 'loop';
    color = '#4CAF50';
    shortform = destination?.includes('Pune') ? 'RT-PN' : destination?.includes('Mumbai') ? 'RT-MU' : 'RT';
    tooltip = `Outstation Round Trip${destination ? ` - ${destination}` : ''}`;
  }
  // Pune Local
  else if (tripType === 'Pune Local' || tripType.includes('Pune Local') || tripType.includes('PUNE_LOCAL')) {
    iconType = 'building';
    color = '#E91E63';
    // Extract hours from the trip type name - try multiple patterns
    let hours = '6'; // Default fallback
    
    // Try to match patterns like "12 Hours", "12H", "12 Hrs", etc.
    const hoursMatch = tripType.match(/(\d+)\s*(?:Hours?|Hrs?|H)\b/i);
    if (hoursMatch) {
      hours = hoursMatch[1];
    } else {
      // Try to match just numbers if they appear to be hour values (6, 9, 12)
      const numberMatch = tripType.match(/\b(6|9|12)\b/);
      if (numberMatch) {
        hours = numberMatch[1];
      }
    }
    
    shortform = `PL-${hours}H`;
    tooltip = `Pune Local - ${hours} Hours`;
  }
  // Custom
  else if (tripType === 'Custom' || tripType.includes('CUSTOM')) {
    iconType = 'settings';
    color = '#9C27B0';
    shortform = 'CUSTOM';
    tooltip = 'Custom Trip';
  }

  return { 
    icon: getTripIconComponent(iconType, color), 
    shortform, 
    tooltip, 
    color 
  };
};

// New function specifically for Redux trip data structure
export const getReduxTripShortform = (trip: any) => {
  const tripType = trip.travelServiceDetail?.travelService?.name || trip.tripType || '';
  const slot = trip.travelServiceDetail?.slot || '';
  const request = trip.request || '';
  const destination = trip.destinationCities || trip.destination || trip.sourceCity || '';

  // For Pune Local, append the slot information to the trip type
  let fullTripType = tripType;
  if (tripType === 'Pune Local' && slot) {
    const hoursMatch = slot.match(/(\d+)_HOURS?/i);
    if (hoursMatch) {
      fullTripType = `${tripType} ${hoursMatch[1]} Hours`;
    }
  }

  // Use the existing getLegacyTripShortform with processed data
  return getLegacyTripShortform(fullTripType, request, destination);
};

interface TripTypeIconProps {
  tripType: string;
  showLabel?: boolean;
  size?: 'small' | 'medium' | 'large';
}

const TripTypeIcon: React.FC<TripTypeIconProps> = ({ tripType, showLabel = true, size = 'medium' }) => {
  const iconType = getTripIcon(tripType);
  const color = getTripColor(tripType);
  const shortform = getTripShortform(tripType);
  const tooltip = getTripTooltip(tripType);
  
  const iconComponent = getTripIconComponent(iconType, color);
  
  const sizeStyles = {
    small: { fontSize: '0.8rem', iconSize: 20 },
    medium: { fontSize: '0.9rem', iconSize: 24 },
    large: { fontSize: '1rem', iconSize: 28 },
  };
  
  return (
    <Tooltip 
      title={tooltip} 
      arrow 
      placement="top"
      componentsProps={{
        tooltip: {
          sx: {
            bgcolor: 'background.paper',
            color: 'text.primary',
            '& .MuiTooltip-arrow': {
              color: 'background.paper',
            },
            boxShadow: 1,
            borderRadius: 1,
            p: 1,
            fontWeight: 'medium',
            fontSize: '0.8rem'
          }
        }
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {iconComponent}
        {showLabel && (
          <span style={{ 
            color, 
            fontWeight: 'bold', 
            fontSize: sizeStyles[size].fontSize, 
            letterSpacing: '0.5px' 
          }}>
            {shortform}
          </span>
        )}
      </Box>
    </Tooltip>
  );
};

export default TripTypeIcon;
